import styles from './index.module.less';
import { Splide, SplideSlide } from '@splidejs/react-splide';
import '@splidejs/react-splide/css';
import { useCallback, useEffect, useRef } from 'react';
import ossImgCompress from '@/utils/ossImgCompress';
import { useGetConfig } from '../../useGetConfig';
import SocialMediaLogo from '../../../SocialMediaLogo';
import { ClassNames } from '@/utils';
import { ProductTracker } from '@/utils/Tracker';
import { throttle } from 'lodash';

// import { isEmpty } from '@/utils';

type MouseProps = {
  /** 是否要开始计算轮播 */
  isCalc: boolean;
  /** 开始位置X */
  startX: number;
  /** X偏移量轮播阈值 */
  offsetThreshold: number;
};

/**
 * 全景主图
 */
const FourViewDisplay = () => {
  const {
    mobxStore: {
      app: { isMobile, webp },
      area: { LANG },
      product: { productDetail, curSelectCommodity },
    },
    fourviewConfig,
  } = useGetConfig();

  /** 在videoWrapRef下面挂了视频元素的句柄videoRef */
  const videoWrapRef = useRef<HTMLDivElement & { videoRef: HTMLVideoElement }>(null);
  const splideRef = useRef<Splide>(null);
  const mouseRef = useRef<HTMLDivElement>(null);
  const gestureRef = useRef<HTMLDivElement>(null);

  const MouseInstance = useRef<MouseProps>({
    isCalc: false,
    startX: 0,
    offsetThreshold: 10,
  });

  // 控制开场动画的开始和结束
  const toggleAniVideo = (type: 'close' | 'start') => {
    if (type === 'close') {
      // 隐藏开场动画
      videoWrapRef.current?.classList.remove(styles.active);
      // 显示轮播模块
      splideRef.current?.splideRef?.current?.classList?.add(styles.active);
    }
    if (type === 'start') {
      if (!videoWrapRef.current?.videoRef || !splideRef.current) return;
      // 重置视频时间
      videoWrapRef.current.videoRef.currentTime = 0;
      videoWrapRef.current.videoRef?.play();
      // 显示开场动画
      videoWrapRef.current?.classList.add(styles.active);

      // 隐藏轮播模块
      splideRef.current?.splideRef?.current?.classList?.remove(styles.active);
      // 重置轮播模块到第一张
      splideRef.current?.splide?.go(0);
    }
  };

  /**
   * @description 检查移动端用户是否操作过四视图
   */
  const checkUsrGesture = () => {
    const op_fourview = localStorage.getItem('op_fourview');
    if (isMobile && !op_fourview) {
      gestureRef.current?.classList.add(styles.show);
      setTimeout(() => {
        gestureRef.current?.classList.remove(styles.show);
      }, 5000);
    }
  };

  // 添加鼠标位置处理函数
  const handleMouseMove = useCallback(
    throttle((e: MouseEvent) => {
      if (mouseRef.current?.dataset.isindiv !== 'true') return;
      const rect = splideRef.current?.splide?.root?.getBoundingClientRect();
      if (!rect) return;

      const elementX = e.clientX - rect.left;
      const elementY = e.clientY - rect.top;

      mouseRef.current?.style.setProperty('left', `${elementX}px`);
      mouseRef.current?.style.setProperty('top', `${elementY}px`);
    }, 16), // 约60fps的更新频率
    []
  );

  // 绑定光标移动事件
  useEffect(() => {
    const splideEle = splideRef.current?.splide?.root;
    if (!splideEle) return;
    splideEle.addEventListener('mousemove', handleMouseMove);

    return () => {
      splideEle.removeEventListener('mousemove', handleMouseMove);
    };
  }, [handleMouseMove]);

  //初始化操作
  useEffect(() => {
    // 开场动画后的切换————隐藏开场动画视频、显示轮播模块
    if (!videoWrapRef.current) return;

    const videoElement = videoWrapRef.current.childNodes[0];
    if (!(videoElement instanceof HTMLVideoElement)) return;

    videoWrapRef.current.videoRef = videoElement;
    const handleEnded = () => {
      toggleAniVideo('close');
      checkUsrGesture();
    };
    videoElement.addEventListener('ended', handleEnded);

    // 如果浏览器不支持自动播放则自动隐藏开场动画
    videoWrapRef.current.videoRef?.play().catch(() => {
      toggleAniVideo('close');
      checkUsrGesture();
    });

    return () => {
      videoElement.removeEventListener('ended', handleEnded);
    };
  }, []);

  // 绑定滑动事件
  useEffect(() => {
    if (!splideRef.current) return;
    const splideEle = splideRef.current?.splide?.root;

    // 用户是否曾经操作过四视图
    const op_fourview = localStorage.getItem('op_fourview');

    /**
     * @description 开始滑动
     * 记录开始的位置X、开始计算、鼠标样式显示
     */
    const handleStart = (e: MouseEvent | TouchEvent): void => {
      if (MouseInstance.current && mouseRef.current) {
        MouseInstance.current.startX = 'clientX' in e ? e.clientX : e.changedTouches[0].clientX;
        MouseInstance.current.isCalc = true;
        mouseRef.current?.classList.add(styles.active);
      }

      ProductTracker.productPageImages({
        action: 'image_drag_angle',
        data: {
          url_key: productDetail?.url_key,
          name: curSelectCommodity?.name,
          id: productDetail?.id,
        },
        category: 'product_image',
      });

      // 用户操作后隐藏手势提示并记录
      if (isMobile && !op_fourview) {
        localStorage.setItem('op_fourview', '1');
        gestureRef.current?.classList.remove(styles.show);
      }
    };

    /**
     * @description 滑动中
     * 计算滑动方向，滑动距离超过阈值则触发滑动
     */
    const handleMoveIn = (e: MouseEvent | TouchEvent): void => {
      e?.preventDefault(); // 阻止默认行为——防止用户拖动页面
      const xOffset = 'clientX' in e ? e.clientX : e.changedTouches[0].clientX;
      if (
        Math.abs(MouseInstance?.current?.startX - xOffset) > MouseInstance.current?.offsetThreshold &&
        MouseInstance.current.isCalc
      ) {
        const goNext = MouseInstance?.current?.startX - xOffset > 0; // 确定滑动方向
        MouseInstance.current.startX = xOffset; // 重置开始位置

        if (goNext) {
          splideRef.current?.splide?.go('>');
        } else {
          splideRef.current?.splide?.go('<');
        }
      }
    };

    const handEnd = (): void => {
      if (MouseInstance.current.isCalc) {
        MouseInstance.current.isCalc = false;
        mouseRef.current?.classList.remove(styles.active);
      }
    };

    const mouseEnter = () => {
      if (!mouseRef.current) return;
      mouseRef.current.dataset.isindiv = 'true';
      mouseRef.current?.style.setProperty('height', '90px');
      mouseRef.current?.style.setProperty('width', '90px');
    };

    const mouseLeave = () => {
      if (!mouseRef.current) return;
      mouseRef.current.dataset.isindiv = 'false';
      mouseRef.current?.style.setProperty('height', '0');
      mouseRef.current?.style.setProperty('width', '0');
    };

    // PC端事件绑定
    if (!isMobile) {
      splideEle?.addEventListener('mousedown', handleStart);
      splideEle?.addEventListener('mousemove', handleMoveIn);
      splideEle?.addEventListener('mouseup', handEnd);
      splideEle?.addEventListener('mouseleave', handEnd);
      splideEle?.addEventListener('mouseenter', mouseEnter);
      splideEle?.addEventListener('mouseleave', mouseLeave);
    }

    // MOB端事件绑定
    if (isMobile) {
      splideEle?.addEventListener('touchstart', handleStart);
      splideEle?.addEventListener('touchmove', handleMoveIn);
      splideEle?.addEventListener('touchend', handEnd);
    }

    return () => {
      splideEle?.removeEventListener('mousedown', handleStart);
      splideEle?.removeEventListener('mousemove', handleMoveIn);
      splideEle?.removeEventListener('mouseup', handEnd);
      splideEle?.removeEventListener('touchstart', handleStart);
      splideEle?.removeEventListener('touchmove', handleMoveIn);
      splideEle?.removeEventListener('touchend', handEnd);
      splideEle?.removeEventListener('mouseenter', mouseEnter);
      splideEle?.removeEventListener('mouseleave', mouseLeave);
    };
  }, []);

  return (
    <section className={styles.fourViewWrap}>
      <div ref={videoWrapRef} className={ClassNames([styles.playAniVideo, styles.active])}>
        <video
          // autoPlay
          // loop
          disablePictureInPicture
          muted
          playsInline
          controls={false}
          poster={ossImgCompress(fourviewConfig?.startPoster ?? '', { webp })}
        >
          <source src={fourviewConfig?.startVideo} type='video/mp4' />
        </video>
      </div>

      <div className={styles.socialLogoWrap}>
        <SocialMediaLogo />
      </div>

      {isMobile && (
        <div ref={gestureRef} className={styles.gestureWrap}>
          <div className={styles.gesture}>
            <img
              className={styles.arrow}
              src='https://res.insta360.com/static/48a6dbd9d588bd053f8385bc2a9c125c/Frame%201973772132.png'
            />
            <img
              className={styles.hands}
              src='https://res.insta360.com/static/5d172b49795524fc9dc870bbf03c4451/Frame%201973772131.png'
            />
          </div>
          <div className={styles.text}>{LANG['productNew.Display.gesture_text']}</div>
        </div>
      )}

      <Splide
        ref={splideRef}
        options={{
          arrows: false,
          pagination: false,
          heightRatio: 1.51,
          type: 'fade',
          rewind: true,
          drag: false,
          speed: 0,
        }}
        className={styles.slideWrap}
      >
        {fourviewConfig?.ani36Fps?.map((url) => {
          return (
            <SplideSlide className={styles.slideItem} key={url}>
              <img
                src={ossImgCompress(
                  url,
                  isMobile
                    ? { webp: false, width: 1200, height: 1200, quality: 100 }
                    : { webp: false, width: 1736, height: 1135 }
                )}
              />
            </SplideSlide>
          );
        })}
      </Splide>

      <div
        className={styles.rePlayBtn}
        onClick={() => {
          toggleAniVideo('start');
          ProductTracker.productPageImages({
            action: 'angle_replay_btn_click',
            data: {
              url_key: productDetail?.url_key,
              name: curSelectCommodity?.name,
              id: curSelectCommodity?.id,
            },
            category: 'product_image',
          });
        }}
      >
        <img src='https://res.insta360.com/static/8213ffcc2099a78b8bca4a9ebd19740c/%E9%87%8D%E7%BD%AE.png' alt='' />
      </div>
      <div ref={mouseRef} className={styles.customMouse}>
        <span className={styles.mouseText}>{LANG['productNew.Display.drag']}</span>
      </div>
    </section>
  );
};

export default FourViewDisplay;
